import { SplitPane } from "@focus/focusUI";
import React, { useEffect, useMemo, useState } from "react";
import {
  SettingsMenu,
  type ConfigurationApp,
  type ConfigurationGroup,
} from "../components/Settings/SettingsMenu";
import { ROLES, useRoles } from "../hooks/useRoles";
import { useHashParams } from "../hooks/useHashParams";
import { ApiKeysContainer } from "./ApiKeysContainer";
import { OperatorConfigContainer } from "./OperatorConfigContainer";

const configurationGroups: ConfigurationGroup[] = [
  {
    id: "system",
    label: "System",
    apps: [
      {
        name: "operator-config",
        label: "Operator Config",
        icon: "user",
        description: "Operator configuration schema",
        container: OperatorConfigContainer,
        required: [ROLES.USER],
      },
    ],
  },
  {
    id: "access",
    label: "Access",
    apps: [
      {
        name: "api-keys",
        label: "API Keys",
        icon: "key",
        description: "Service credentials",
        container: ApiKeysContainer,
        required: [ROLES.ADMIN, ROLES.API_KEYS],
      },
    ],
  },
];

const userCanAccessApp = (app: ConfigurationApp, roles: string[]) => {
  if (!app.required || app.required.length === 0) return true;
  return app.required.some((role) => roles.includes(role));
};

export const SettingsContainer: React.FC = () => {
  const { getParam, setParam } = useHashParams();
  const { roles } = useRoles();

  const allowedGroups = useMemo(
    () =>
      configurationGroups
        .map((group) => ({
          ...group,
          apps: group.apps.filter((app) => userCanAccessApp(app, roles)),
        }))
        .filter((group) => group.apps.length > 0),
    [roles],
  );

  const allowedApps = useMemo(
    () => allowedGroups.flatMap((group) => group.apps),
    [allowedGroups],
  );

  const getSectionFromHash = (): string | undefined => {
    const param = getParam("section");
    const isAllowed = allowedApps.some((app) => app.name === param);

    if (param && isAllowed) {
      return param;
    }

    return allowedApps[0]?.name;
  };

  const [selectedSection, setSelectedSection] = useState<string | undefined>(
    getSectionFromHash,
  );

  useEffect(() => {
    const isSelectedAllowed = allowedApps.some(
      (app) => app.name === selectedSection,
    );

    if (isSelectedAllowed) {
      return;
    }

    const fallbackSection = allowedApps[0]?.name;
    setSelectedSection(fallbackSection);

    if (fallbackSection) {
      setParam("section", fallbackSection);
    }
  }, [allowedApps, selectedSection, setParam]);

  const selectedApp = allowedApps.find((app) => app.name === selectedSection);
  const SelectedContainer = selectedApp?.container;

  const handleSelectSection = (section: string) => {
    setSelectedSection(section);
    setParam("section", section);
  };

  return (
    <div className="foc-content flex flex-col overflow-hidden">
      <main className="min-h-0 flex-1 overflow-hidden bg-slate-50/40 p-5">
        {SelectedContainer ? (
          <section className="relative h-full min-h-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <SplitPane
              defaultSize={300}
              maxSize={440}
              minSize={260}
              pane1Style={{
                minHeight: 0,
                overflow: "hidden",
              }}
              pane2Style={{
                minHeight: 0,
                overflow: "hidden",
              }}
              resizerStyle={{
                background: "rgb(226 232 240)",
                cursor: "col-resize",
                width: 1,
                zIndex: 1,
              }}
              split="vertical"
              style={{
                position: "absolute",
              }}
            >
              <SettingsMenu
                groups={allowedGroups}
                onSelect={handleSelectSection}
                selected={selectedSection}
              />

              <div className="h-full min-h-0 overflow-hidden bg-white">
                <SelectedContainer />
              </div>
            </SplitPane>
          </section>
        ) : (
          <section className="flex h-full items-center justify-center rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <div className="max-w-md">
              <div className="text-xs font-semibold uppercase text-slate-500">
                Access
              </div>
              <h2 className="mt-2 text-xl font-bold text-slate-950">
                No settings available
              </h2>
              <p className="mt-2 text-sm font-medium text-slate-500">
                Your current role does not include access to any settings
                section.
              </p>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};
